import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  BorderRadiusProps,
  JustifyContentProps,
  MinWidthProps,
  SizeProps,
  SpaceProps,
} from 'styled-system';
import {
  backgroundColor,
  borderRadius,
  justifyContent,
  minWidth,
  size,
  space,
} from 'styled-system';

import { useLocalization } from '../../providers/LocaleProvider';
import type { BackgroundColorProps, FillProps } from '../../theme/system-types';
import type { ThemeHeightProps, ThemeMinWidthProps } from '../../theme/system-utilities';
import { fill, themeBottom, themeHeight, themeTop, themeWidth } from '../../theme/system-utilities';
import { Button } from '../Button';
import type { IconProps } from '../Icon';
import Icon from '../Icon';

interface SearchInputButtonProps {
  iconVariant?: 'primary' | 'secondary';
}

const ICON_SIZE = 14;

const SearchIcon = styled(Icon)<IconProps & SizeProps & FillProps & SpaceProps & MinWidthProps>`
  ${size};
  ${space};
  ${fill};
  ${themeTop};
  ${themeBottom};
  ${minWidth};
`;

const searchIconStyles = {
  my: 'auto',
  mr: 'sp_0',
  size: ICON_SIZE,
  minWidth: ICON_SIZE,
};

const ButtonContainer = styled(Button)<
  BorderRadiusProps &
    SpaceProps &
    BackgroundColorProps &
    ThemeMinWidthProps &
    ThemeHeightProps &
    JustifyContentProps
>`
  ${space};
  ${backgroundColor};
  ${borderRadius};
  ${themeHeight};
  ${themeWidth};
  ${justifyContent};
`;

const buttonContainerStyles: ComponentProps<typeof ButtonContainer> = {
  my: 'auto',
  mx: 'sp_2',
  px: 'sp_8',
  bg: 'sys.primary.background.default',
  borderRadius: 'circle',
  themeHeight: 'sp_32',
  themeWidth: 'sp_32',
  justifyContent: 'center',
};

const buttonContainerSecondaryStyles: ComponentProps<typeof ButtonContainer> = {
  kind: 'secondary',
  bg: 'sys.neutral.background.default',
  p: 'sp_8',
};

const SearchInputButton = ({ iconVariant }: SearchInputButtonProps) => {
  const { t } = useLocalization();

  return (
    <ButtonContainer
      data-cnstrc-search-submit-btn=""
      type="submit"
      tabIndex={-1}
      aria-label={t('h24_search_button_text')}
      {...buttonContainerStyles}
      {...(iconVariant === 'secondary' ? buttonContainerSecondaryStyles : {})}
    >
      <SearchIcon
        name="searchFillSmall"
        ariaLabel={t('h24_search_button_text')}
        fill={
          iconVariant === 'secondary' ? 'sys.neutral.icon.default' : 'sys.neutral.icon.inverted'
        }
        {...searchIconStyles}
      />
    </ButtonContainer>
  );
};

export default SearchInputButton;
